import { FaRegEdit } from "react-icons/fa"; 
import { RiSendPlaneFill } from "react-icons/ri"; 
import { FaPlus, FaTrash } from "react-icons/fa";
import { BiCamera } from "react-icons/bi";
import { RiAttachment2 } from "react-icons/ri";
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { scheduleMessage, getScheduled, subscribe, cancelScheduled, loadScheduledFromBackend } from '../../utils/messageScheduler';
import { compressImages } from '../../utils/imageUtils';
import MessageImages from '../../components/MessageImages';

const API_URL = process.env.REACT_APP_API_URL;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [paymentIncome, setPaymentIncome] = useState('');
  const [paymentPoint, setPaymentPoint] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [showsendPointsModal, setShowsendPointsModal] = useState(false);
  const [message, setMessage] = useState('');
  const [newPoints, setNewPoints] = useState('');
  const [newsendPoints, setNewsendPoints] = useState('');
  const [displayPoints, setDisplayPoints] = useState('');
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editAvatar, setEditAvatar] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('userID');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    gender: 'male',
    photoLink: ''
  });
  const [generatedUserID, setGeneratedUserID] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [videoRef, setVideoRef] = useState(null);
  const [canvasRef, setCanvasRef] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Scheduling state
  const [enableSchedule, setEnableSchedule] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [scheduledItems, setScheduledItems] = useState([]);
  const [scheduledItemsFromBackend, setScheduledItemsFromBackend] = useState([]);
  const [previousScheduledItems, setPreviousScheduledItems] = useState([]);
  // Image upload state
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [isCompressingImages, setIsCompressingImages] = useState(false);
  const [dismissedSentNotifications, setDismissedSentNotifications] = useState(() => {
    // Load dismissed notifications from localStorage on mount
    try {
      const saved = localStorage.getItem('dismissedSentNotifications');
      if (saved) {
        const parsed = JSON.parse(saved);
        return new Set(parsed);
      }
    } catch (err) {
      console.error('Failed to load dismissed notifications:', err);
    }
    return new Set();
  });

  // Resolve recipient name for scheduled panel
  const resolveRecipientName = (id) => {
    const u = users.find(x => x._id === id);
    return u?.name || '不明';
  };

  // Emoji picker data
  const emojis = [
    '😊', '😄', '😍', '🥰', '😘', '😉', '😋', '😎', '🤗', '🤩',
    '👍', '👎', '👌', '✌️', '🤞', '👏', '🙌', '🤝', '💪', '👊',
    '❤️', '💖', '💕', '💗', '💓', '💝', '💘', '💞', '💟', '💌',
    '🌹', '🌸', '🌺', '🌷', '🌼', '🌻', '💐', '🌱', '🌿', '🍀',
    '🎉', '🎊', '🎈', '🎂', '🎁', '🎀', '🎪', '🎭', '🎨', '🎬',
    '☀️', '🌙', '⭐', '🌟', '✨', '💫', '🌈', '☁️', '🌤️', '🌅'
  ];

  // Fetch users from API
  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Load scheduled messages from backend on mount
    const loadScheduled = async () => {
      try {
        await loadScheduledFromBackend('admin:user-mgmt');
        
        // Process any due scheduled messages first
        const token = localStorage.getItem('token');
        if (token) {
          try {
            // Trigger processing of due messages
            const processResponse = await axios.post(
              `${API_URL}/api/admin/scheduled-messages/process-due?source=admin:user-mgmt`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Show notification if messages were sent
            if (processResponse.data.recentlySent > 0) {
              alert(`${processResponse.data.recentlySent}件の予約メッセージを送信しました`);
            }
          } catch (err) {
            console.error('Failed to process due scheduled messages:', err);
          }
          
          // Fetch updated scheduled messages
          const response = await axios.get(
            `${API_URL}/api/admin/scheduled-messages?source=admin:user-mgmt`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const allItems = response.data.data || [];
          setScheduledItemsFromBackend(allItems);
          setPreviousScheduledItems(allItems);
          
          // Load dismissed notifications from localStorage
          try {
            const saved = localStorage.getItem('dismissedSentNotifications');
            if (saved) {
              const parsed = JSON.parse(saved);
              setDismissedSentNotifications(new Set(parsed));
            }
          } catch (err) {
            console.error('Failed to load dismissed notifications:', err);
          }
        }
      } catch (err) {
        console.error('Failed to load scheduled messages:', err);
      }
    };
    loadScheduled();

    // subscribe to scheduler updates for this context
    const unsubscribe = subscribe((items) => {
      setScheduledItems(items.filter((it) => it.source === 'admin:user-mgmt'));
    });
    setScheduledItems(getScheduled().filter((it) => it.source === 'admin:user-mgmt'));
    
    // Refresh backend data periodically and process due messages
    const refreshInterval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          // Process due messages
          try {
            const processResponse = await axios.post(
              `${API_URL}/api/admin/scheduled-messages/process-due?source=admin:user-mgmt`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Show notification if messages were sent (only if user is on the page)
            if (processResponse.data.recentlySent > 0) {
              // Use a less intrusive notification
              console.log(`${processResponse.data.recentlySent}件の予約メッセージを送信しました`);
            }
          } catch (err) {
            console.error('Failed to process due scheduled messages:', err);
          }
          
          // Fetch updated scheduled messages
          const response = await axios.get(
            `${API_URL}/api/admin/scheduled-messages?source=admin:user-mgmt`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const allItems = response.data.data || [];
          
          // Update previous state
          setPreviousScheduledItems(allItems);
          
          // Store all items (including sent ones) for display
          setScheduledItemsFromBackend(allItems);
          
          // Also sync with messageScheduler
          await loadScheduledFromBackend('admin:user-mgmt');
        }
      } catch (err) {
        console.error('Failed to refresh scheduled messages:', err);
      }
    }, 30000); // Refresh every 30 seconds
    
    return () => {
      unsubscribe();
      clearInterval(refreshInterval);
    };
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      // Fallback to mock data if API fails
      const mockUsers = [
        // {
        //   _id: '1',
        //   userID: '12345678',
        //   name: '田中 花子',
        //   gender: 1, // 1 for female, 0 for male
        //   birthday: '1990-05-15',
        //   annual_income: { annualIncome: '400万円' },
        //   occupation: { occupaionName: '会社員' },
        //   points: 1500,
        //   sendPoints: 500,
        //   photoLink: 'https://via.placeholder.com/80x80/ff6b9d/ffffff?text=花',
        //   email: 'tanaka@example.com',
        //   role: 'user',
        //   favoriteUserIds: ['2', '3'],
        //   inquiries: []
        // },
        // {
        //   _id: '2',
        //   userID: '87654321',
        //   name: '佐藤 太郎',
        //   gender: 0, // 0 for male
        //   birthday: '1988-12-03',
        //   annual_income: { annualIncome: '500万円' },
        //   occupation: { occupaionName: 'エンジニア' },
        //   points: 2200,
        //   sendPoints: 800,
        //   photoLink: 'https://via.placeholder.com/80x80/4ecdc4/ffffff?text=太',
        //   email: 'sato@example.com',
        //   role: 'user',
        //   favoriteUserIds: ['1'],
        //   inquiries: []
        // }
      ];
      setUsers(mockUsers);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = (user) => {
    setSelectedUser(user);
    setShowMessageModal(true);
  };

  const handleEditPoints = (user) => {
    setSelectedUser(user);
    setNewPoints((user.points ?? 0).toString());
    setDisplayPoints((user.points ?? 0).toString());
    setNewsendPoints((user.sendPoints ?? 0).toString());
    setEditName(user.name || '');
    setEditEmail(user.email || '');
    setEditAvatar(user?.photoLink?.photodatas || user?.photoLink || '');
    setAvatarFile(null);
    setNewPassword('');
    setConfirmPassword('');
    setShowPointsModal(true);
  };

  const openAddPaymentModal = () => {
    setPaymentIncome('');
    setPaymentPoint('');
    setPaymentNotes('');
    setShowAddPaymentModal(true);
  };

  const confirmAddPayment = async () => {
    try {
      const incomeNum = parseInt(paymentIncome, 10);
      const pointNum = parseInt(paymentPoint, 10);
      if (isNaN(incomeNum) || incomeNum < 0 || isNaN(pointNum) || pointNum < 0) {
        alert('入金金額とポイント数は0以上の数値で入力してください');
        return;
      }

      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/payment-history`, {
        userID: selectedUser.userID,
        income: incomeNum,
        point: pointNum,
        notes: paymentNotes
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Reflect updated points locally
      const updatedUsers = users.map(u => (
        u._id === selectedUser._id ? { ...u, points: (u.points || 0) + pointNum } : u
      ));
      setUsers(updatedUsers);
      setSelectedUser(prev => prev ? { ...prev, points: (prev.points || 0) + pointNum } : prev);

      // Update the newPoints input field and display to reflect the new total
      const newTotal = ((selectedUser?.points || 0) + pointNum).toString();
      setNewPoints(newTotal);
      setDisplayPoints(newTotal);

      setShowAddPaymentModal(false);
      alert('入金履歴を保存し、ポイントを加算しました');
    } catch (error) {
      console.error('Error creating payment history:', error);
      alert('入金履歴の保存に失敗しました: ' + (error.response?.data?.message || error.message));
    }
  };


  const handleEditsendPoints = (user) => {
    setSelectedUser(user);
    setNewsendPoints((user.sendPoints ?? 0).toString());
    setShowsendPointsModal(true);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setEditAvatar(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeAvatar = () => {
    setEditAvatar('');
    setAvatarFile(null);
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/') && !file.type.startsWith('video/'));
    
    if (imageFiles.length === 0) {
      alert('画像ファイルを選択してください（動画ファイルは除く）');
      return;
    }

    if (selectedImages.length + imageFiles.length > 10) {
      alert('画像は最大10枚まで選択できます');
      return;
    }

    setIsCompressingImages(true);
    try {
      const compressedImages = await compressImages(imageFiles);
      setSelectedImages(prev => [...prev, ...compressedImages]);
      setImagePreviewUrls(prev => [...prev, ...compressedImages]);
      // NOTE: Do not inject placeholder text into textarea; images are shown via previews/thumbnails.
    } catch (error) {
      console.error('Error compressing images:', error);
      alert('画像の圧縮に失敗しました');
    } finally {
      setIsCompressingImages(false);
    }
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const sendMessage = async () => {
    if (!message.trim() && selectedImages.length === 0) {
      alert('メッセージまたは画像を入力してください');
      return;
    }

    try {
      // If scheduled for the future, push to scheduler and exit
      const isFuture = enableSchedule && scheduledAt && new Date(scheduledAt) > new Date();
      if (isFuture) {
        await scheduleMessage({
          recipients: [selectedUser._id],
          content: message,
          images: selectedImages,
          scheduledAt,
          source: 'admin:user-mgmt',
          type: 'direct',
        });
        alert('メッセージを予約しました');
        
        // Refresh scheduled messages list
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const response = await axios.get(
              `${API_URL}/api/admin/scheduled-messages?source=admin:user-mgmt`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setScheduledItemsFromBackend(response.data.data || []);
          }
        } catch (err) {
          console.error('Failed to refresh scheduled messages:', err);
        }
        
        setShowMessageModal(false);
        setMessage('');
        setSelectedImages([]);
        setImagePreviewUrls([]);
        setSelectedUser(null);
        setEnableSchedule(false);
        setScheduledAt('');
        return;
      }

      // Send immediately
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/messages/recipientId`, {
        userId: selectedUser._id,
        content: message,
        images: selectedImages
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setShowMessageModal(false);
      setMessage('');
      setSelectedImages([]);
      setImagePreviewUrls([]);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const updateCurrentPoints = async () => {
    try {
      // Validate password if provided
      if (newPassword && newPassword !== confirmPassword) {
        alert('パスワードが一致しません');
        return;
      }

      if (newPassword && newPassword.length < 6) {
        alert('パスワードは6文字以上で入力してください');
        return;
      }

      const token = localStorage.getItem('token');
      
      // Prepare the update data
      const updateData = {
        points: parseInt(newPoints),
        sendPoints: parseInt(newsendPoints),
        name: editName,
        email: editEmail
      };

      // Add password if provided
      if (newPassword) {
        updateData.password = newPassword;
      }

      // If there's a new avatar file, include it
      if (avatarFile) {
        updateData.photoLink = editAvatar;
      } else if (editAvatar === '') {
        // If avatar was removed, send empty string
        updateData.photoLink = '';
      }

      const response = await axios.put(`${API_URL}/api/admin/users/${selectedUser._id}`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      console.log("response",response);
      // Update the local state with the new user data
      setUsers(users.map(user => 
        
        user._id === selectedUser._id ? response.data.user : user,
      ));

      alert('ユーザー情報を更新しました');
      setShowPointsModal(false);
      setNewPoints('');
      setShowsendPointsModal(false);
      setNewsendPoints('');
      setEditName('');
      setEditEmail('');
      setEditAvatar('');
      setAvatarFile(null);
      setNewPassword('');
      setConfirmPassword('');
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      alert('ユーザー情報の更新に失敗しました: ' + (error.response?.data?.message || error.message));
    }
  };

  const addEmoji = (emoji) => {
    setMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const generateUserCredentials = async () => {

    try {
      // API call to send message
      
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/auth/uniuqeGenerate`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setGeneratedUserID(res.data.userID);
      setGeneratedPassword(res.data.password);
    } catch (error) {
      console.error('Error sending message:', error);
    }
    
  };

  const handleAddUser = () => {
    generateUserCredentials();
    setShowAddUserModal(true);
  };

  const createUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const userData = {
        userID: generatedUserID,
        password: generatedPassword,
        name: newUser.name,
        points: "100",
        sendPoints: "20",
        email: newUser.email,
        gender: newUser.gender,
        photoLink: newUser.photoLink || null,
        role: 'user'
      };

      const response = await axios.post(`${API_URL}/api/admin/users`, userData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Add the new user to the local state
      setUsers([...users, response.data]);
      
      // Reset form and close modal
      setNewUser({
        name: '',
        email: '',
        gender: 'male',
        photoLink: ''
      });
      setShowAddUserModal(false);
      setGeneratedUserID('');
      setGeneratedPassword('');
      setPhotoFile(null);
    } catch (error) {
      console.error('Error creating user:', error);
      alert('ユーザーの作成に失敗しました。');
    }
  };

  // Avatar upload helpers (copied from ProfileEditModal)
  const compressImage = (dataUrl, maxWidth = 2048, maxHeight = 2048, quality = 0.8) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        let { width, height } = img;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedDataUrl);
      };
      img.src = dataUrl;
    });
  };

  const uploadPhoto = async (photoDataString) => {
    try {
      setUploadingPhoto(true);
      const token = localStorage.getItem('token');
      // For test-user creation, we don't persist the photo yet.
      // We mirror the behavior by storing preview in state and later submit as URL/base64.
      setNewUser(prev => ({ ...prev, photoLink: photoDataString }));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('画像ファイルを選択してください。');
        event.target.value = '';
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const compressedImage = await compressImage(e.target.result, 2048, 2048, 0.8);
          await uploadPhoto(compressedImage);
        } catch (error) {
          alert('画像の処理に失敗しました。別のファイルを試してください。');
          event.target.value = '';
          setPhotoFile(null);
        }
      };
      reader.onerror = () => {
        alert('ファイルの読み込みに失敗しました。別のファイルを試してください。');
        event.target.value = '';
        setPhotoFile(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraStream(stream);
      setShowCamera(true);
      if (videoRef) {
        videoRef.srcObject = stream;
      }
    } catch (error) {
      alert('カメラへのアクセスに失敗しました');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const takePhoto = async () => {
    if (videoRef && canvasRef) {
      const context = canvasRef.getContext('2d');
      const videoWidth = videoRef.videoWidth;
      const videoHeight = videoRef.videoHeight;
      if (videoWidth > 2048 || videoHeight > 2048) {
        alert('カメラの解像度が高すぎます。別のカメラまたは設定を試してください。');
        stopCamera();
        return;
      }
      canvasRef.width = videoWidth;
      canvasRef.height = videoHeight;
      context.drawImage(videoRef, 0, 0);
      const photoData = canvasRef.toDataURL('image/jpeg');
      try {
        const compressedPhoto = await compressImage(photoData, 2048, 2048, 0.8);
        await uploadPhoto(compressedPhoto);
      } catch (error) {
        alert('写真の処理に失敗しました。もう一度試してください。');
      }
      stopCamera();
    }
  };

  const clearAvatar = () => {
    setNewUser(prev => ({ ...prev, photoLink: '' }));
    setPhotoFile(null);
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('このユーザーを削除しますか？この操作は取り消せません。')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Remove the user from local state
      setUsers(users.filter(user => user._id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('ユーザーの削除に失敗しました。');
    }
  };

  const getGenderLabel = (gender) => {
    // console.log("=================??",gender);
    return gender === "female" ? '女' : '男';
  };

  const getGenderColor = (gender) => {
    return gender === "female" ? '#ff6b9d' : '#75adecff';
  };

  const handleDismissSentNotification = (itemId) => {
    setDismissedSentNotifications(prev => {
      const newSet = new Set([...prev, itemId]);
      // Save to localStorage for persistence
      try {
        localStorage.setItem('dismissedSentNotifications', JSON.stringify([...newSet]));
      } catch (err) {
        console.error('Failed to save dismissed notifications:', err);
      }
      return newSet;
    });
  };

  const sentNotifications = scheduledItemsFromBackend
    .filter(item => {
      // Include sent messages that haven't been dismissed
      return item.status === 'sent' && !dismissedSentNotifications.has(item._id);
    })
    .sort((a, b) => {
      // Sort by scheduledAt (most recent first)
      return new Date(b.scheduledAt) - new Date(a.scheduledAt);
    });

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    switch (searchField) {
      case 'userID':
        // Handle multiple user IDs separated by commas
        if (searchTerm.includes(',')) {
          const searchIds = searchTerm.split(',').map(id => id.trim().toLowerCase()).filter(id => id.length > 0);
          return searchIds.some(id => user.userID.toLowerCase().includes(id));
        }
        return user.userID.toLowerCase().includes(searchLower);
      case 'email':
        return user.email.toLowerCase().includes(searchLower);
      case 'name':
        return user.name.toLowerCase().includes(searchLower);
      case 'gender':
        return getGenderLabel(user.gender).toLowerCase().includes(searchLower);
      default:
        return true;
    }
  });

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>読み込み中...</div>
      </div>
    );
  }
  
  return (
    <div>
      <h2 style={{ color: '#2c3e50', marginBottom: '30px' }}>ユーザー管理</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontSize: '16px', color: '#7f8c8d' }}>
              総ユーザー数: <strong>{filteredUsers.length}</strong>人
            </span>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '5px'
              }}
            >
              <option value="userID">ユーザーID</option>
              {/* <option value="email">メールアドレス</option>
              <option value="name">名前</option> */}
              <option value="gender">性別</option>
            </select>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchField === 'userID' ? "検索... (複数IDはカンマ区切り)" : "検索..."}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                width: '200px'
              }}
            />
            <button
              onClick={handleAddUser}
              style={{
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '5px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '14px'
              }}
            >
              <FaPlus /> ユーザー追加
            </button>
          </div>
        </div>
      </div>

      {/* Scheduled items panel */}
      <div style={{
        marginBottom: '20px',
        background: '#f8f9fa',
        border: '1px solid #eee',
        borderRadius: '8px',
        padding: '10px'
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>⏳ 予約中のメッセージ</div>
        {sentNotifications.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
            {sentNotifications.map((item) => (
              <div key={item._id} style={{
                border: '1px solid #c3e6cb',
                borderRadius: '4px',
                padding: '8px 10px',
                background: '#d4edda'
              }}>
                <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                  <span style={{ color: '#155724', fontWeight: 'bold' }}>✅ メッセージが予約時間に正確に送信されました</span>
                </div>
                <div style={{ fontSize: '12px', color: '#155724' }}>
                  <span style={{ marginRight: '6px' }}>🕒 {new Date(item.scheduledAt).toLocaleString('ja-JP')}</span>
                  <span style={{ color: '#7f8c8d' }}>{item.type}</span>
                  <span style={{ marginLeft: '8px', color: '#34495e' }}>宛先: {resolveRecipientName(Array.isArray(item.recipients) ? item.recipients[0]?._id || item.recipients[0] : item.recipients[0]?._id || item.recipients)}</span>
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                  <button
                    onClick={() => handleDismissSentNotification(item._id)}
                    style={{
                      background: '#28a745',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    確認
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {scheduledItemsFromBackend.filter(item => item.status === 'pending').length === 0 && sentNotifications.length === 0 ? (
          <div style={{ color: '#7f8c8d', fontSize: '12px' }}>現在、予約はありません</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {scheduledItemsFromBackend
              .filter(item => item.status === 'pending')
              .map((item) => (
              <div key={item._id} style={{
                border: '1px solid #eee',
                borderRadius: '4px',
                padding: '6px 8px',
                background: item.status === 'failed' ? '#fdecea' : '#fcf3cf'
              }}>
                <div style={{ fontSize: '12px' }}>
                  <span style={{ marginRight: '6px' }}>🕒 {new Date(item.scheduledAt).toLocaleString('ja-JP')}</span>
                  <span style={{ color: '#7f8c8d' }}>{item.type}</span>
                  <span style={{ marginLeft: '8px', color: '#34495e' }}>宛先: {resolveRecipientName(Array.isArray(item.recipients) ? item.recipients[0]?._id || item.recipients[0] : item.recipients[0]?._id || item.recipients)}</span>
                  {item.status === 'failed' && (
                    <span style={{ color: '#e74c3c', marginLeft: '6px' }}>失敗: {item.lastError}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                  <button
                    onClick={() => { if (window.confirm('この予約を取り消しますか？')) { cancelScheduled(item._id); } }}
                    style={{
                      background: '#e74c3c',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    取り消し
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          backgroundColor: 'white',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#34495e', color: 'white' }}>
              <th style={{ padding: '5px', textAlign: 'left' }}>写真</th>
              <th style={{ padding: '5px', textAlign: 'left' }}>ユーザーID</th>
              <th style={{ padding: '5px', textAlign: 'left' }}>名前</th>
              <th style={{ padding: '5px', textAlign: 'left' }}>性別</th>
              {/* <th style={{ padding: '5px', textAlign: 'left' }}>誕生日</th> */}
              <th style={{ padding: '5px', textAlign: 'left' }}>年収</th>
              {/* <th style={{ padding: '5px', textAlign: 'left' }}>職業</th> */}
              <th style={{ padding: '5px', textAlign: 'left' }}>ポイント</th>
              <th style={{ padding: '5px', textAlign: 'left' }}>送信ポイント</th>
              <th style={{ padding: '5px', textAlign: 'left' }}>操作</th>
              <th style={{ padding: '5px', textAlign: 'left' }}>削除</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              // console.log("!!!!!!!!!!",user),
              <tr key={user._id} style={{ borderBottom: '1px solid #ecf0f1' }}>
                <td style={{ padding: '5px' }}>
                  <img
                    src={user?.photoLink?.photodatas || user?.photoLink || 'https://placehold.co/600x400/png'}
                    alt={user.name}
                    style={{
                      width: '70px',
                      height: '70px',
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      console.log("----",e);
                      e.target.src = 'https://placehold.co/600x400/png';
                    }}
                  />
                </td>
                <td style={{ padding: '5px', fontFamily: 'monospace' }}>{user.userID}</td>
                <td style={{ padding: '5px', fontWeight: 'bold' }}>{user.name}</td>
                <td style={{ padding: '5px' }}>
                  <span style={{
                    backgroundColor: getGenderColor(user.gender),
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}>
                    {getGenderLabel(user.gender)}
                  </span>
                </td>
                {/* <td style={{ padding: '5px' }}>{user.birthday}</td> */}
                {/* <td style={{ padding: '5px' }}>
                  {user.annual_income ? `¥${user.annual_income.annualIncome}` : '未設定'}
                </td>
                <td style={{ padding: '5px' }}>
                  {user.occupation ? user.occupation.occupaionName : '未設定'}
                </td> */}
                <td style={{ padding: '5px' }}>
                  <span style={{
                    backgroundColor: '#f39c12',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}>
                    {user.sendPoints}pt
                  </span>
                </td>
                {/* <td style={{ padding: '5px', justifyContent:'center'}}>
                  <div style={{ display: 'flex',  flexDirection: 'column' }}>
                    <button
                        onClick={() => handleEditsendPoints(user)}
                        style={{
                          backgroundColor: '#e67e22',
                          color: 'white',
                          border: 'none',
                          // padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '20px'
                        }}
                      >
                      <FaRegEdit />
                    </button>
                  </div>
                </td> */}

                <td style={{ padding: '5px' }}>
                  <span style={{
                    backgroundColor: '#f39c12',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}>
                    {user.points}pt
                  </span>
                </td>
                <td style={{ padding: '5px', justifyContent:'center'}}>
                  <div style={{ display: 'flex',  flexDirection: 'column' }}>
                    <button
                        onClick={() => handleEditPoints(user)}
                        style={{
                          backgroundColor: '#e67e22',
                          color: 'white',
                          border: 'none',
                          // padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '20px'
                        }}
                      >
                      <FaRegEdit />
                    </button>
                  </div>
                </td>
                <td style={{ padding: '1px' }}>
                  {user.role !== 'admin' && (
                    <div style={{ display: 'flex',  flexDirection: 'column' }}>
                      <button
                        onClick={() => handleSendMessage(user)}
                        style={{
                          backgroundColor: 'white',
                          color: '#3498db',
                          border: '1px',
                          borderColor: '#565657ff',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '20px'
                        }}
                      >
                        <RiSendPlaneFill style={{marginTop:'5px'}} />
                      </button>
                      
                    </div>
                  )}
                </td>
                <td style={{ padding: '1px' }}>
                  {user.role !== 'admin' && (
                    <div style={{ display: 'flex',  flexDirection: 'column' }}>
                      <button
                        onClick={() => deleteUser(user._id)}
                        style={{
                          // backgroundColor: '#e74c3c',
                          color: '#e74c3c',
                          border: 'none',
                          // padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '16px'
                        }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        
      {/* Message Modal */}
      {showMessageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            width: '500px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginBottom: '20px' }}>
              {selectedUser?.name}さんにメッセージを送信
            </h3>
            
            <div style={{ marginBottom: '3px' }}>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="メッセージを入力してください..."
                style={{
                  width: '95%',
                  height: '150px',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  resize: 'vertical',
                  marginBottom: '10px'
                }}
              />
            </div>

            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '10px',
                padding: '20px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                zIndex: 1001,
                maxWidth: '400px',
                maxHeight: '300px',
                overflow: 'auto'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: '10px' }}>
                  {emojis.map((emoji, index) => (
                    <button
                      key={index}
                      onClick={() => addEmoji(emoji)}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        fontSize: '20px',
                        cursor: 'pointer',
                        padding: '5px',
                        borderRadius: '5px',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowEmojiPicker(false)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    backgroundColor: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    padding: '5px 10px',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>
            )}

            {/* Image Upload + Emoji controls (match ProfileSearch layout) */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <label
                    style={{
                      border: '1px solid #3498db',
                      color: 'black',
                      padding: '8px 10px 0px 10px ',
                      borderRadius: '5px',
                      cursor: (isCompressingImages || selectedImages.length >= 10) ? 'not-allowed' : 'pointer',
                      fontSize: '20px',
                      display: 'inline-block',
                      opacity: (isCompressingImages || selectedImages.length >= 10) ? 0.6 : 1
                    }}
                  >
                    <RiAttachment2 />
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageSelect}
                      disabled={isCompressingImages || selectedImages.length >= 10}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <label
                    style={{
                      border: '1px solid #27ae60',
                      color: 'black',
                      padding: '8px 10px 0px 10px ',
                      borderRadius: '5px',
                      cursor: (isCompressingImages || selectedImages.length >= 10) ? 'not-allowed' : 'pointer',
                      fontSize: '20px',
                      display: 'inline-block',
                      opacity: (isCompressingImages || selectedImages.length >= 10) ? 0.6 : 1
                    }}
                  >
                    <BiCamera />
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleImageSelect}
                      disabled={isCompressingImages || selectedImages.length >= 10}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
                <div>
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    style={{
                      backgroundColor: '#ff9800',
                      color: 'white',
                      border: 'none',
                      padding: '8px 8px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      marginRight: '10px'
                    }}
                  >
                    😊 絵文字
                  </button>
                </div>
              </div>
              {isCompressingImages && (
                <div style={{ color: '#666', fontSize: '12px', marginBottom: '10px' }}>
                  画像を圧縮中...
                </div>
              )}
              {imagePreviewUrls.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                  gap: '10px',
                  marginTop: '10px'
                }}>
                  {imagePreviewUrls.map((url, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img
                        src={url}
                        alt={`Preview ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '5px',
                          border: '1px solid #ddd'
                        }}
                      />
                      <button
                        onClick={() => removeImage(index)}
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Scheduling controls */}
            <div style={{ marginBottom: '10px' }}>
              <label>
                <input
                  type="checkbox"
                  checked={enableSchedule}
                  onChange={(e) => setEnableSchedule(e.target.checked)}
                  style={{ marginRight: '6px' }}
                />
                後で送信を予約する
              </label>
            </div>
            {enableSchedule && (
              <div style={{ marginBottom: '10px' }}>
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => {
                  setShowMessageModal(false);
                  setMessage('');
                  setSelectedImages([]);
                  setImagePreviewUrls([]);
                }}
                style={{
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={sendMessage}
                disabled={!message.trim() && selectedImages.length === 0}
                style={{
                  backgroundColor: (message.trim() || selectedImages.length > 0) ? '#27ae60' : '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: (message.trim() || selectedImages.length > 0) ? 'pointer' : 'not-allowed'
                }}
              >
                {enableSchedule && scheduledAt && new Date(scheduledAt) > new Date() ? '予約' : '送信'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Points Edit Modal */}
      {showPointsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            width: '400px',
            maxWidth: '90%'
          }}>
            {/* <h3 style={{ marginBottom: '20px' }}>
              ユーザー情報編集
            </h3> */}
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                アバター:
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {editAvatar && (
                  <div style={{ position: 'relative' }}>
                    <img
                      src={editAvatar}
                      alt="Avatar Preview"
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2px solid #ddd'
                      }}
                    />
                    <button
                      type="button"
                      onClick={removeAvatar}
                      style={{
                        position: 'absolute',
                        top: '-5px',
                        right: '-5px',
                        backgroundColor: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    style={{ marginBottom: '10px' }}
                  />
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {editAvatar ? '新しい画像を選択するか、×ボタンで削除' : '画像を選択してください'}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                ユーザー名:
              </label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                style={{
                  width: '95%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                メールアドレス:
              </label>
              <input
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                style={{
                  width: '95%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                新しいパスワード (変更する場合のみ入力):
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="新しいパスワードを入力"
                style={{
                  width: '95%',
                  padding: '10px',
                  border: newPassword ? 
                    (newPassword.length >= 6 ? '1px solid #27ae60' : '1px solid #e74c3c') : 
                    '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              />
              {newPassword && (
                <div style={{ 
                  fontSize: '12px', 
                  marginTop: '5px',
                  color: newPassword.length >= 6 ? '#27ae60' : '#e74c3c'
                }}>
                  {newPassword.length >= 6 ? '✓ パスワードの長さは適切です' : '✗ パスワードは6文字以上で入力してください'}
                </div>
              )}
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                パスワード確認:
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="パスワードを再入力"
                style={{
                  width: '95%',
                  padding: '10px',
                  border: confirmPassword && newPassword ? 
                    (confirmPassword === newPassword ? '1px solid #27ae60' : '1px solid #e74c3c') : 
                    '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              />
              {confirmPassword && newPassword && (
                <div style={{ 
                  fontSize: '12px', 
                  marginTop: '5px',
                  color: confirmPassword === newPassword ? '#27ae60' : '#e74c3c'
                }}>
                  {confirmPassword === newPassword ? '✓ パスワードが一致しています' : '✗ パスワードが一致しません'}
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 'bold' }}>
                現在のポイント: <span style={{ color: '#e67e22', fontWeight: 'bold' }}>{displayPoints}pt</span>
              </label>
              <button
                type="button"
                onClick={openAddPaymentModal}
                title="入金を追加"
                style={{
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  padding: '6px 10px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <FaPlus /> 入金
              </button>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              {/* <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                新しいポイント:
              </label> */}
              <input
                type="number"
                value={newPoints}
                onChange={(e) => {
                  setNewPoints(e.target.value);
                  setDisplayPoints(e.target.value);
                }}
                min="0"
                style={{
                  width: '95%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              />
            </div>
            
            <div style={{  }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                現在の送信ポイント: <span style={{ color: '#e67e22', fontWeight: 'bold' }}>{selectedUser?.sendPoints}pt</span>
              </label>
            </div>
            <div style={{ marginBottom: '20px' }}>
              {/* <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                新しい送信ポイント:
              </label> */}
              <input
                type="number"
                value={newsendPoints}
                onChange={(e) => setNewsendPoints(e.target.value)}
                min="0"
                style={{
                  width: '95%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowPointsModal(false)}
                style={{
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={updateCurrentPoints}
                disabled={!editName.trim() || !editEmail.trim() || !newPoints.trim() || parseInt(newPoints) < 0 || !newsendPoints.trim() || parseInt(newsendPoints) < 0 || (newPassword && newPassword !== confirmPassword) || (newPassword && newPassword.length < 6)}
                style={{
                  backgroundColor: (!editName.trim() || !editEmail.trim() || !newPoints.trim() || parseInt(newPoints) < 0 || !newsendPoints.trim() || parseInt(newsendPoints) < 0 || (newPassword && newPassword !== confirmPassword) || (newPassword && newPassword.length < 6)) ? '#95a5a6' : '#e67e22',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: (!editName.trim() || !editEmail.trim() || !newPoints.trim() || parseInt(newPoints) < 0 || !newsendPoints.trim() || parseInt(newsendPoints) < 0 || (newPassword && newPassword !== confirmPassword) || (newPassword && newPassword.length < 6)) ? 'not-allowed' : 'pointer'
                }}
              >
                更新
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddPaymentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            width: '420px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>
              入金を追加
            </h3>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                入金金額: <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="number"
                value={paymentIncome}
                onChange={(e) => setPaymentIncome(e.target.value)}
                min="0"
                placeholder="例: 10000"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                ポイント数: <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="number"
                value={paymentPoint}
                onChange={(e) => setPaymentPoint(e.target.value)}
                min="0"
                placeholder="例: 1000"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                備考:
              </label>
              <textarea
                rows="3"
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="任意の備考を入力"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowAddPaymentModal(false)}
                style={{
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={confirmAddPayment}
                disabled={!(paymentIncome && parseInt(paymentIncome, 10) >= 0 && paymentPoint && parseInt(paymentPoint, 10) >= 0)}
                style={{
                  backgroundColor: (paymentIncome && parseInt(paymentIncome, 10) >= 0 && paymentPoint && parseInt(paymentPoint, 10) >= 0) ? '#27ae60' : '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: (paymentIncome && parseInt(paymentIncome, 10) >= 0 && paymentPoint && parseInt(paymentPoint, 10) >= 0) ? 'pointer' : 'not-allowed'
                }}
              >
                確認
              </button>
            </div>
          </div>
        </div>
      )}


      {/* sendPoints Edit Modal */}
      {showsendPointsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginBottom: '20px' }}>
              {selectedUser?.name}さんの送信ポイント変更
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                現在のポイント: <span style={{ color: '#e67e22', fontWeight: 'bold' }}>{selectedUser?.sendPoints}pt</span>
              </label>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                新しいポイント:
              </label>
              <input
                type="number"
                value={newsendPoints}
                onChange={(e) => setNewsendPoints(e.target.value)}
                min="0"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowsendPointsModal(false)}
                style={{
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={updateCurrentPoints}
                disabled={!newsendPoints.trim() || parseInt(newsendPoints) < 0}
                style={{
                  backgroundColor: (!newsendPoints.trim() || parseInt(newsendPoints) < 0) ? '#95a5a6' : '#e67e22',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: (!newsendPoints.trim() || parseInt(newsendPoints) < 0) ? 'not-allowed' : 'pointer'
                }}
              >
                更新
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            width: '500px',
            maxWidth: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>
              テストユーザー追加
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                ユーザーID (自動生成):
              </label>
              <input
                type="text"
                value={generatedUserID}
                readOnly
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  backgroundColor: '#f8f9fa',
                  fontFamily: 'monospace',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                パスワード (自動生成):
              </label>
              <input
                type="text"
                value={generatedPassword}
                readOnly
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  backgroundColor: '#f8f9fa',
                  fontFamily: 'monospace',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                名前: <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                value={newUser.name}
                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                placeholder="ユーザー名を入力"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                メールアドレス: <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                placeholder="メールアドレスを入力"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                性別: <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                value={newUser.gender}
                onChange={(e) => setNewUser({...newUser, gender: e.target.value})}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              >
                <option value="male">男性</option>
                <option value="female">女性</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                プロフィール写真
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px', fontStyle: 'italic' }}>
                    最大サイズ: 2048x2048ピクセル (画像ファイルのみ)
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <button
                      onClick={startCamera}
                      disabled={uploadingPhoto}
                      style={{
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '5px',
                        cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                        opacity: uploadingPhoto ? 0.6 : 1
                      }}
                    >
                      {uploadingPhoto ? 'アップロード中...' : 'カメラで撮影'}
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      id="adduser-photo-upload"
                      disabled={uploadingPhoto}
                    />
                    <label
                      htmlFor="adduser-photo-upload"
                      style={{
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '5px',
                        cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                        display: 'inline-block',
                        opacity: uploadingPhoto ? 0.6 : 1
                      }}
                    >
                      {uploadingPhoto ? 'アップロード中...' : 'ファイルから選択'}
                    </label>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  {(newUser.photoLink) && (
                    <img
                      src={newUser.photoLink}
                      alt="Preview"
                      style={{
                        width: '130px',
                        height: '150px',
                        borderRadius: '10%',
                        objectFit: 'cover',
                        border: '2px solid #ddd'
                      }}
                    />
                  )}
                  {(newUser.photoLink) && (
                    <button
                      onClick={clearAvatar}
                      disabled={uploadingPhoto}
                      style={{
                        backgroundColor: '#f44336',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '5px',
                        cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                        opacity: uploadingPhoto ? 0.6 : 1
                      }}
                    >
                      削除
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => {
                  setShowAddUserModal(false);
                  setNewUser({
                    name: '',
                    email: '',
                    gender: 'male',
                    photoLink: ''
                  });
                  setGeneratedUserID('');
                  setGeneratedPassword('');
                }}
                style={{
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={createUser}
                disabled={!newUser.name.trim() || !newUser.email.trim()}
                style={{
                  backgroundColor: (!newUser.name.trim() || !newUser.email.trim()) ? '#95a5a6' : '#27ae60',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: (!newUser.name.trim() || !newUser.email.trim()) ? 'not-allowed' : 'pointer'
                }}
              >
                ユーザー作成
              </button>
            </div>
          </div>
        </div>
      )}

      {showCamera && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '10px',
            textAlign: 'center'
          }}>
            <h3>カメラで撮影</h3>
            <video
              ref={setVideoRef}
              autoPlay
              style={{ width: '400px', height: '300px', marginBottom: '10px' }}
            />
            <canvas
              ref={setCanvasRef}
              style={{ display: 'none' }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={takePhoto}
                style={{
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                撮影
              </button>
              <button
                onClick={stopCamera}
                style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
            )}
     </div>
 
     
   );
 };
 
 export default UserManagement; 